import { useParams, useLocation, Link } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { formatOrderAmount, getEstimatedDelivery } from '../utils/formatPrice';
import { trackPurchase, remarketingPurchasePage } from '../utils/analytics';
import { clearAttribution } from '../utils/urlParams';
import { fireConversionEvent } from '../utils/googleAds';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const GUEST_PROMPT_DELAY = 30; // seconds

const OrderSuccessPage = () => {
  const { orderId } = useParams();
  const { state } = useLocation();
  const { user } = useAuth();
  const { clearCart } = useCart();
  const order = state?.order;

  const [copied, setCopied] = useState(false);
  const [guestCountdown, setGuestCountdown] = useState(GUEST_PROMPT_DELAY);
  const [showGuestPrompt, setShowGuestPrompt] = useState(false);

  useEffect(() => {
    clearCart(); // safety net — ensures cart is empty even on direct URL access
    if (!order) return;
    // Router state survives a reload, so guard against re-firing the purchase
    // tracking (and double-counting the conversion) for the same order.
    const firedKey = `purchase_tracked_${orderId}`;
    try {
      if (sessionStorage.getItem(firedKey)) return;
      sessionStorage.setItem(firedKey, '1');
    } catch {
      // sessionStorage unavailable — fall through and track
    }
    trackPurchase({ ...order, orderId });
    remarketingPurchasePage({ ...order, orderId });
    fireConversionEvent(orderId, order.total || order.totalAmount || 0, order.currency || 'INR');
    clearAttribution();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 30-second countdown then show guest prompt (only for non-logged-in users)
  useEffect(() => {
    if (user) return;
    if (guestCountdown <= 0) { setShowGuestPrompt(true); return; }
    const id = setTimeout(() => setGuestCountdown(c => c - 1), 1000);
    return () => clearTimeout(id);
  }, [guestCountdown, user]);

  const copyOrderId = useCallback(() => {
    if (!orderId) return;
    navigator.clipboard.writeText(orderId).then(() => {
      setCopied(true);
      toast.success('Order ID copied!');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error('Could not copy — please copy manually.');
    });
  }, [orderId]);

  return (
    <>
      <Helmet><title>Order Confirmed — Skinn Thrive</title></Helmet>
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        {/* Success animation */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-fade-in">
          <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h1 className="text-3xl font-black text-gray-900 mb-2">Order Confirmed! 🎉</h1>
        <p className="text-gray-500 mb-6">Thank you for shopping with Skinn Thrive. Your order is being processed.</p>

        <div className="bg-secondary rounded-2xl p-6 mb-8 text-left border border-primary/10">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500 text-xs uppercase font-semibold mb-0.5">Order ID</p>
              <div className="flex items-center gap-2">
                <p className="font-bold text-gray-900 text-base">{orderId}</p>
                <button
                  onClick={copyOrderId}
                  title="Copy order ID"
                  className="p-1 text-ink-muted hover:text-primary transition-colors rounded"
                >
                  {copied ? (
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase font-semibold mb-0.5">Payment</p>
              <p className="font-semibold text-gray-800">{order?.paymentMethod === 'COD' ? '💵 Cash on Delivery' : '💳 Prepaid'}</p>
            </div>
            {order?.email && (
              <div>
                <p className="text-gray-500 text-xs uppercase font-semibold mb-0.5">Confirmation Email</p>
                <p className="font-medium text-gray-800">{order.email}</p>
              </div>
            )}
            {order?.paymentMethodDiscount > 0 && (
              <div>
                <p className="text-gray-500 text-xs uppercase font-semibold mb-0.5">Payment Discount</p>
                <p className="font-semibold text-emerald-600 text-sm">
                  −{formatOrderAmount(order.paymentMethodDiscount, order.currency)}
                  <span className="text-xs text-gray-400 ml-1">({order.paymentMethod === 'COD' ? 'COD' : 'Prepaid'} offer)</span>
                </p>
              </div>
            )}
            {order?.total && (
              <div>
                <p className="text-gray-500 text-xs uppercase font-semibold mb-0.5">Order Total</p>
                <p className="font-bold text-primary text-base">{formatOrderAmount(order.total, order.currency)}</p>
              </div>
            )}
          </div>

          {order?.address && (
            <div className="mt-4 pt-4 border-t border-primary/10">
              <p className="text-gray-500 text-xs uppercase font-semibold mb-1">Delivering to</p>
              <p className="text-sm text-gray-800">{order.address.name} · {order.address.phone}</p>
              <p className="text-sm text-gray-600">{order.address.line1}, {order.address.city}, {order.address.state} {order.address.pincode}</p>
            </div>
          )}
        </div>

        {/* Loyalty — points earned / redeemed */}
        {user && (order?.loyaltyEarnPreview > 0 || order?.loyaltyPointsRedeemed > 0) && (
          <div className="rounded-2xl border border-purple-200 mb-6 overflow-hidden">
            <div className="px-5 py-3 flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #5B21B6, #7C3AED)' }}>
              <span className="text-xl">⭐</span>
              <p className="text-sm font-bold text-white">Reward Points</p>
            </div>
            <div className="bg-purple-50 px-5 py-4 space-y-2">
              {order.loyaltyEarnPreview > 0 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-purple-800">
                    {order.paymentMethod === 'COD'
                      ? 'Points earned on this order'
                      : 'Points you\'ll earn (credited after payment clears)'}
                  </p>
                  <span className="text-sm font-bold text-purple-700">
                    +{order.loyaltyEarnPreview} pts
                    {order.loyaltyRupeesPerPoint > 0 && (
                      <span className="text-xs font-normal text-purple-500 ml-1">
                        (₹{(order.loyaltyEarnPreview * Number(order.loyaltyRupeesPerPoint)).toFixed(0)} value)
                      </span>
                    )}
                  </span>
                </div>
              )}
              {order.loyaltyPointsRedeemed > 0 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-purple-800">Points redeemed for discount</p>
                  <span className="text-sm font-bold text-purple-700">
                    −{order.loyaltyPointsRedeemed} pts
                  </span>
                </div>
              )}
              <p className="text-xs text-purple-500 pt-1 border-t border-purple-200">
                View your full points history in{' '}
                <Link to="/account/rewards" className="underline font-medium">My Rewards</Link>
              </p>
            </div>
          </div>
        )}

        {/* Delivery estimate */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🚀</span>
            <div className="text-left">
              <p className="font-semibold text-green-800">Estimated Delivery</p>
              <p className="text-sm text-green-700">{getEstimatedDelivery(order?.paymentMethod || 'COD')}</p>
            </div>
          </div>
        </div>

        {/* Items */}
        {order?.items && (
          <div className="card p-5 mb-8 text-left">
            <h3 className="font-bold text-gray-900 mb-4">Items Ordered</h3>
            <div className="space-y-3">
              {order.items.map((item, i) => (
                <div key={i} className="flex gap-3">
                  <img
                    src={item.imageUrl || item.image || item.images?.[0]}
                    alt={item.title}
                    className="w-16 h-16 rounded-lg object-cover bg-gray-100 shrink-0"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.style.visibility = 'hidden'; }}
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-800 line-clamp-2">{item.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Qty: {item.quantity} × {formatOrderAmount(item.price, order.currency)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Guest account prompt — appears after 30s countdown */}
        {!user && (
          <div className={`mb-8 rounded-2xl border transition-all duration-500
            ${showGuestPrompt
              ? 'border-primary/30 bg-primary-light p-5'
              : 'border-border bg-surface p-4'}`}>
            {showGuestPrompt ? (
              <div className="text-center animate-fade-in">
                <p className="text-base font-bold text-ink mb-1">Save your order history!</p>
                <p className="text-sm text-ink-soft mb-4">
                  Create a free account to track orders, earn rewards, and re-order with one click.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <Link
                    to="/register"
                    state={{ prefillEmail: order?.email }}
                    className="btn-primary btn-sm"
                  >
                    Create Free Account
                  </Link>
                  <button
                    onClick={() => setShowGuestPrompt(false)}
                    className="text-xs text-ink-muted hover:text-ink transition-colors"
                  >
                    No thanks
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 text-left">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-sm font-bold text-primary">
                  {guestCountdown}
                </div>
                <p className="text-xs text-ink-soft">
                  Create a free account to track orders and re-order easily.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/track-order" className="btn-primary">Track My Order</Link>
          <Link to="/shop" className="btn-outline">Continue Shopping</Link>
          {!user && !showGuestPrompt && (
            <Link to="/register" className="btn-ghost">Create Account</Link>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderSuccessPage;
